import { Dialog } from '@headlessui/react';
import { X, Loader2, CheckCircle, Copy, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';
import { formatPrice } from '@/lib/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';

interface PaymentMethodDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  orderTotal: number;
  shippingTotal: number;
  vatAmount: number;
  customerEmail: string;
  customerName: string;
  storeInfo: any;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>, type: 'order' | 'shipping') => void;
  orderPaymentReceipt: File | null;
  shippingPaymentReceipt: File | null;
  paymentMethod: 'bank' | 'paystack' | 'monnify' | 'stripe';
  onPaymentMethodChange: (method: 'bank' | 'paystack' | 'monnify' | 'stripe') => void;
}

export function PaymentMethodDialog({
  open,
  onClose,
  onConfirm,
  orderTotal,
  shippingTotal,
  vatAmount,
  customerEmail,
  customerName,
  storeInfo,
  onFileUpload,
  orderPaymentReceipt,
  shippingPaymentReceipt,
  paymentMethod,
  onPaymentMethodChange,
}: PaymentMethodDialogProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    if (location.state?.paymentStatus === 'success') {
      setPaymentSuccess(true);
      const timer = setTimeout(() => {
        onConfirm();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [location.state, onConfirm]);

  const handleCopyAccountNumber = (accountNumber: string) => {
    navigator.clipboard.writeText(accountNumber);
    toast.success('Account number copied to clipboard');
  };

  const handleStripePayment = async () => {
    try {
      setIsProcessing(true);
      
      if (!customerEmail) {
        toast.error('Customer email is required');
        return;
      }
      
      // Get current domain
      const domain = window.location.hostname;
      
      // Calculate total for this payment
      const paymentTotal = orderTotal + shippingTotal + vatAmount;
      
      const response = await fetch(`https://${domain}/stripe-intent.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: Math.round(paymentTotal * 100),
          currency: 'CAD',
          email: customerEmail,
          domain: domain,
          metadata: {
            orderId: `ord_${Math.floor(Math.random() * 1000000000)}`,
            customerName: customerName
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create payment intent');
      }

      const { clientSecret, id: paymentIntentId, publishableKey } = await response.json();

      navigate('/stripe-payment', {
        state: {
          clientSecret,
          paymentIntentId,
          publishableKey,
          amount: total,
          email: customerEmail,
          name: customerName
        },
      });
      onClose();
    } catch (error) {
      console.error('Payment initialization error:', error);
      toast.error('Failed to initialize payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const showSeparateShipping = storeInfo?.mb_active === 1 && storeInfo?.sepShipping === 1 && shippingTotal > 0;
  const total = orderTotal + shippingTotal + vatAmount;

  const handleConfirmPayment = async () => {
    if (paymentMethod === 'bank' && !orderPaymentReceipt) {
      toast.error('Please upload payment receipt');
      return;
    }

    if (paymentMethod === 'bank' && showSeparateShipping && !shippingPaymentReceipt) {
      toast.error('Please upload shipping payment receipt');
      return;
    }

    setIsProcessing(true);

    try {
      if (paymentMethod === 'stripe') {
        await handleStripePayment();
      } else if (paymentMethod === 'paystack') {
        const handler = window.PaystackPop.setup({
          key: storeInfo?.pays_pk,
          email: customerEmail,
          amount: total * 100,
          currency: 'NGN',
          ref: `ord_${Math.floor(Math.random() * 1000000000)}`,
          callback: (response: any) => {
            if (response.status === 'success') {
              onConfirm();
            } else {
              toast.error('Payment failed');
              setIsProcessing(false);
            }
          },
          onClose: () => {
            toast.error('Payment cancelled');
            setIsProcessing(false);
          }
        });
        handler.openIframe();
      } else if (paymentMethod === 'monnify') {
        const paymentRef = `ord_${Math.floor(Math.random() * 1000000000)}`;
        window.MonnifySDK.initialize({
          amount: total,
          currency: "NGN",
          reference: paymentRef,
          customerName: customerName,
          customerEmail: customerEmail,
          apiKey: storeInfo?.monify_api,
          contractCode: storeInfo?.monify_contract,
          paymentDescription: "Order Payment",
          isTestMode: true,
          metadata: {
            "name": customerName,
          },
          paymentMethods: ["CARD", "ACCOUNT_TRANSFER"],
          onComplete: function(response: any) {
            if (response.status === "SUCCESS") {
              onConfirm();
            } else {
              toast.error('Payment failed');
            }
            setIsProcessing(false);
          },
          onClose: function() {
            toast.error('Payment cancelled');
            setIsProcessing(false);
          }
        });
      } else {
        await onConfirm();
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full rounded-lg bg-white dark:bg-gray-800">
          <div className="p-6 border-b dark:border-gray-700">
            <div className="flex items-center justify-between">
              <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                {paymentSuccess ? 'Payment Successful' : 'Payment Method'}
              </Dialog.Title>
              {!paymentSuccess && !isProcessing && (
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[calc(100vh-16rem)] overflow-y-auto p-6 custom-scrollbar">
            {paymentSuccess ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                  Payment Successful!
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Your order is being processed...
                </p>
                <div className="bg-yellow-50 dark:bg-yellow-900/30 p-4 rounded-lg">
                  <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                    Please do not close this page. Your order is being submitted automatically.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">Order Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Subtotal:</span>
                      <span className="font-medium">{formatPrice(orderTotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Shipping:</span>
                      <span className="font-medium">{formatPrice(shippingTotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">VAT:</span>
                      <span className="font-medium">{formatPrice(vatAmount)}</span>
                    </div>
                    <div className="pt-2 border-t dark:border-gray-600">
                      <div className="flex justify-between font-medium">
                        <span className="text-gray-900 dark:text-white">Total:</span>
                        <span className="text-gray-900 dark:text-white">{formatPrice(total)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Stripe Payment */}
                  {storeInfo?.stripeActive === 1 && (
                    <div className="p-4 border rounded-lg dark:border-gray-700">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={paymentMethod === 'stripe'}
                          onChange={() => onPaymentMethodChange('stripe')}
                          className="mr-2"
                        />
                        <span className="font-medium dark:text-white">Pay with Stripe</span>
                      </label>
                      {paymentMethod === 'stripe' && (
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          Secure payment processing with credit/debit cards
                        </p>
                      )}
                    </div>
                  )}

                  {/* Bank Transfer */}
                  {storeInfo?.mb_active === 1 && (
                    <div className="p-4 border rounded-lg dark:border-gray-700">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={paymentMethod === 'bank'}
                          onChange={() => onPaymentMethodChange('bank')}
                          className="mr-2"
                        />
                        <span className="font-medium dark:text-white">Bank Transfer</span>
                      </label>
                      {paymentMethod === 'bank' && (
                        <div className="mt-4 space-y-4">
                          <div className="space-y-2">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Order Amount: <b>{formatPrice(orderTotal + vatAmount)}</b>
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Bank: <span className="font-medium">{storeInfo.m_bank}</span>
                            </p>
                            <div className="flex items-center space-x-2">
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                Account Number: <span className="font-medium">{storeInfo.m_ac_num}</span>
                              </p>
                              <button
                                onClick={() => handleCopyAccountNumber(storeInfo.m_ac_num)}
                                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                              >
                                <Copy className="h-4 w-4 text-gray-500" />
                              </button>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Account Name: <span className="font-medium">{storeInfo.m_ac_name}</span>
                            </p>
                          </div>

                          {showSeparateShipping && (
                            <div className="mt-4 pt-4 border-t dark:border-gray-700">
                              <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-2">
                                Shipping Amount: <b>{formatPrice(shippingTotal)}</b>
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
                                {storeInfo.sepShippingAcn}
                              </p>
                              <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                                Note: If shipping payment is combined with order payment, it will not be refunded and the order will not be shipped.
                              </p>
                            </div>
                          )}

                          {/* File Upload Section */}
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Upload Payment Receipt{showSeparateShipping ? ' (Order)' : ''}
                              </label>
                              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg dark:border-gray-600">
                                <div className="space-y-1 text-center">
                                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                                  <div className="flex text-sm text-gray-600 dark:text-gray-400">
                                    <label className="relative cursor-pointer rounded-md font-medium text-brand-primary dark:text-brand-light hover:text-brand-light">
                                      <span>Upload a file</span>
                                      <input
                                        type="file"
                                        className="sr-only"
                                        onChange={(e) => onFileUpload(e, 'order')}
                                        accept="image/*,.pdf"
                                      />
                                    </label>
                                  </div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    PNG, JPG, PDF up to 2MB
                                  </p>
                                </div>
                              </div>
                              {orderPaymentReceipt && (
                                <p className="mt-2 text-sm text-green-500">
                                  Selected: {orderPaymentReceipt.name}
                                </p>
                              )}
                            </div>

                            {showSeparateShipping && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Upload Shipping Payment Receipt
                                </label>
                                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg dark:border-gray-600">
                                  <div className="space-y-1 text-center">
                                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                                    <div className="flex text-sm text-gray-600 dark:text-gray-400">
                                      <label className="relative cursor-pointer rounded-md font-medium text-brand-primary dark:text-brand-light hover:text-brand-light">
                                        <span>Upload a file</span>
                                        <input
                                          type="file"
                                          className="sr-only"
                                          onChange={(e) => onFileUpload(e, 'shipping')}
                                          accept="image/*,.pdf"
                                        />
                                      </label>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      PNG, JPG, PDF up to 2MB
                                    </p>
                                  </div>
                                </div>
                                {shippingPaymentReceipt && (
                                  <p className="mt-2 text-sm text-green-500">
                                    Selected: {shippingPaymentReceipt.name}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Paystack */}
                  {storeInfo?.pays_active === 1 && (
                    <div className="p-4 border rounded-lg dark:border-gray-700">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={paymentMethod === 'paystack'}
                          onChange={() => onPaymentMethodChange('paystack')}
                          className="mr-2"
                        />
                        <span className="font-medium dark:text-white">Pay with Paystack</span>
                      </label>
                      {paymentMethod === 'paystack' && (
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          Secure payment with Paystack
                        </p>
                      )}
                    </div>
                  )}

                  {/* Monnify */}
                  {storeInfo?.monify_active === 1 && (
                    <div className="p-4 border rounded-lg dark:border-gray-700">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={paymentMethod === 'monnify'}
                          onChange={() => onPaymentMethodChange('monnify')}
                          className="mr-2"
                        />
                        <span className="font-medium dark:text-white">Pay with Monnify</span>
                      </label>
                      {paymentMethod === 'monnify' && (
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          Secure payment with Monnify
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {!paymentSuccess && (
            <div className="p-6 border-t dark:border-gray-700">
              <div className="flex justify-end space-x-4">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleConfirmPayment}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    `Pay ${formatPrice(total)}`
                  )}
                </Button>
              </div>
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}